import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { HRM_MANAGE_PERMISSIONS } from '@/constant/hr/manage-permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { SkillCategoryTab } from '../../components/skill-category-tab/skill-category-tab';
import { SkillSubCategoryTab } from '../../components/skill-sub-category-tab/skill-sub-category-tab';
import { SkillTab } from '../../components/skill-tab/skill-tab';
import { PageHeader } from '@/components/core/page-header/page-header';

/** The `?tab=` values; the old category / sub-category pages redirect here with theirs. */
export const SKILL_MANAGER_TABS = {
  CATEGORIES: 'categories',
  SUB_CATEGORIES: 'sub-categories',
  SKILLS: 'skills',
} as const;

/**
 * Skill Manager: skill categories, skill sub-categories and the skills under them, one tab each. A tab
 * shows for whoever may manage it; each tab has its own create button, filters and dialogs.
 */
export const SkillManagerPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = [
    {
      value: SKILL_MANAGER_TABS.CATEGORIES,
      label: t('SKILL_CATEGORIES'),
      allowed: useHasPermission(HRM_MANAGE_PERMISSIONS.SKILL_CATEGORY),
      content: <SkillCategoryTab />,
    },
    {
      value: SKILL_MANAGER_TABS.SUB_CATEGORIES,
      label: t('SKILL_SUB_CATEGORIES'),
      allowed: useHasPermission(HRM_MANAGE_PERMISSIONS.SKILL_SUB_CATEGORY),
      content: <SkillSubCategoryTab />,
    },
    {
      value: SKILL_MANAGER_TABS.SKILLS,
      label: t('SKILLS'),
      allowed: useHasPermission(HRM_MANAGE_PERMISSIONS.SKILL),
      content: <SkillTab />,
    },
  ].filter((tab) => tab.allowed);

  // An unknown or not-permitted ?tab= falls back to the first tab the person may open.
  const requested = searchParams.get('tab');
  const activeTab = tabs.some((tab) => tab.value === requested) ? requested : tabs[0]?.value;

  const handleTabChange = (value: string) =>
    setSearchParams(
      (params) => {
        params.set('tab', value);
        return params;
      },
      { replace: true }
    );

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('SKILL_MANAGER')} />

      {activeTab && (
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="w-full justify-start overflow-x-auto sm:w-auto">
            {tabs.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value}>
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((tab) => (
            <TabsContent key={tab.value} value={tab.value} className="mt-4">
              {tab.content}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};
